<template>
  <div class="cloud-qr-panel">
    <p class="tip warn-tip">
      <strong>备用方式：</strong>服务器无头浏览器截图二维码，环境一致性弱于「独立登录窗口」；
      {{ scanHint }}，Cookie 将保存到服务器。
    </p>
    <div class="btn-row">
      <el-button
        type="primary"
        :loading="loading"
        @click="startLogin"
      >
        开始云端扫码
      </el-button>
      <el-button v-if="sessionId" @click="refreshShot">刷新二维码</el-button>
    </div>
    <p v-if="message" class="status-tip">{{ message }}</p>
    <p v-if="error" class="warn-tip">{{ error }}</p>
    <div v-if="sessionId" class="shot-wrap" v-loading="shotLoading">
      <img
        v-if="screenshotUrl"
        :src="screenshotUrl"
        alt="登录二维码"
        class="shot"
      />
      <p v-else class="tip">等待二维码截图…</p>
    </div>
  </div>
</template>

<script setup>
import { ref, onBeforeUnmount } from 'vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'

const props = defineProps({
  platform: { type: String, required: true },
  accountId: { type: String, required: true },
  scanHint: { type: String, default: '请用手机 App 扫描二维码' },
  apiBase: { type: String, required: true },
})

const emit = defineEmits(['success'])

const loading = ref(false)
const shotLoading = ref(false)
const sessionId = ref('')
const message = ref('')
const error = ref('')
const screenshotUrl = ref('')
let pollTimer = null

const stopPoll = () => {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

const clearSession = async () => {
  await axios.post(`${props.apiBase}/cookies/clear-session`, null, {
    params: { platform: props.platform, account_id: props.accountId },
  })
}

const refreshShot = () => {
  if (!sessionId.value) return
  shotLoading.value = true
  screenshotUrl.value = `${props.apiBase}/cookies/login-server/${sessionId.value}/screenshot?t=${Date.now()}`
  setTimeout(() => { shotLoading.value = false }, 500)
}

const isServerLoginComplete = (data) => {
  if (data?.status !== 'success') return false
  const msg = data.message || ''
  if (msg.includes('正在同步账号资料')) return false
  return Boolean(
    data.display_name
    || data.profile?.display_name
    || msg.includes('Cookie 与资料已保存')
    || msg.includes('已存在于')
    || data.duplicate_merged,
  )
}

const finishSuccess = async (data) => {
  stopPoll()
  loading.value = false
  emit('success', {
    ...data,
    platform: props.platform,
    account_id: data.canonical_account_id || data.account_id || props.accountId,
  })
}

const pollLogin = async () => {
  if (!sessionId.value) return
  try {
    const res = await axios.get(`${props.apiBase}/cookies/login-server/${sessionId.value}`)
    message.value = res.data.message || ''
    error.value = res.data.error || ''
    if (res.data.has_screenshot && res.data.status !== 'success') {
      refreshShot()
    }
    if (isServerLoginComplete(res.data)) {
      await finishSuccess(res.data)
    } else if (res.data.status === 'success' && (res.data.message || '').includes('资料稍后')) {
      await finishSuccess(res.data)
    } else if (res.data.status === 'cancelled') {
      stopPoll()
      loading.value = false
    } else if (res.data.status === 'failed') {
      ElMessage.error(res.data.error || '登录失败')
      stopPoll()
      loading.value = false
    }
  } catch (e) {
    console.error(e)
  }
}

const startLogin = async () => {
  loading.value = true
  message.value = '正在启动服务器浏览器…'
  error.value = ''
  screenshotUrl.value = ''
  stopPoll()
  try {
    await clearSession()
    const res = await axios.post(`${props.apiBase}/cookies/login-server`, null, {
      params: {
        platform: props.platform,
        account_id: props.accountId,
        force_relogin: true,
      },
    })
    sessionId.value = res.data.session_id
    message.value = res.data.message || '请扫描下方二维码'
    pollTimer = setInterval(pollLogin, 2000)
    pollLogin()
  } catch (e) {
    loading.value = false
    message.value = ''
    ElMessage.error(
      e.response?.data?.detail || '无法启动云端扫码登录（请确认服务器已安装 Playwright Chromium）',
    )
  }
}

const cancelLogin = async () => {
  stopPoll()
  const sid = sessionId.value
  sessionId.value = ''
  message.value = ''
  error.value = ''
  screenshotUrl.value = ''
  loading.value = false
  if (!sid) return
  try {
    await axios.post(`${props.apiBase}/cookies/login-server/${sid}/cancel`)
  } catch {
    /* 会话可能已结束 */
  }
}

onBeforeUnmount(() => {
  void cancelLogin()
})

defineExpose({ cancelLogin })
</script>

<style scoped>
.tip { margin: 0; font-size: 13px; line-height: 1.5; color: #606266; }
.warn-tip { font-size: 12px; color: #e6a23c; margin: 0; line-height: 1.5; }
.btn-row { display: flex; gap: 8px; flex-wrap: wrap; margin: 12px 0; }
.status-tip { font-size: 13px; color: #409eff; margin: 8px 0 0; }
.shot-wrap {
  margin-top: 12px;
  min-height: 240px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fafafa;
  padding: 8px;
}
.shot { max-width: 100%; max-height: 360px; }
</style>
